import { fmtClaim, type GbClaim } from './types.js';

// ---------------------------------------------------------------------------
// The ball
// ---------------------------------------------------------------------------
// Gold gradient sphere, dark serif value. Killers are visually distinct
// (oxblood/azure body, skull glyph) so they never read as cash at a
// glance on a phone. Face-down balls show their declared claim once
// made — the whole game is reading the gap between claim and truth.

export type BallState =
  | 'faceUp'      // true value known to everyone
  | 'faceDown'    // hidden, maybe carrying a claim
  | 'yours'       // hidden to others, known to you
  | 'confirmed'   // still in the pool (green shading)
  | 'removed';    // out of the pool (red shading)

export type BallSize = 'sm' | 'md' | 'lg';

// Phone first. Your own hand is `lg`, and at a flat 80px three of them
// plus their captions were most of a 640px screen before the claim
// picker got a look in.
const SIZES: Record<BallSize, string> = {
  sm: 'h-10 w-10 text-[0.55rem] sm:h-12 sm:w-12 sm:text-[0.6rem]',
  md: 'h-14 w-14 text-[0.65rem] sm:h-16 sm:w-16 sm:text-xs',
  lg: 'h-16 w-16 text-xs sm:h-20 sm:w-20 sm:text-sm'
};

export function Ball({
  claim,
  size = 'md',
  state = 'faceUp',
  label,
  selected = false,
  dimmed = false,
  onClick,
  title
}: {
  /** null renders a blank face-down ball. */
  claim: GbClaim | null;
  size?: BallSize;
  state?: BallState;
  /** Small caption under the value (e.g. "claimed"). */
  label?: string;
  selected?: boolean;
  dimmed?: boolean;
  onClick?: () => void;
  title?: string;
}) {
  const isKiller = claim === 'KILLER';
  const faceDown = state === 'faceDown' && claim === null;

  // Body: gold for cash, RED for killers, slate for a blank back.
  // Killers are red with BLACK text — they must be unmistakable at a
  // glance on a phone, and never read as "a slightly different gold".
  const body = faceDown
    ? 'from-ec-panel-2 to-ec-panel text-ec-bone-dim'
    : isKiller
      ? 'from-[#e02a1f] to-[#8c1109] text-black'
      : 'from-ec-brass to-ec-brass-deep text-ec-ink';

  const ring =
    state === 'confirmed'
      ? 'ring-2 ring-ec-go/70'
      : state === 'removed'
        ? 'ring-2 ring-ec-oxblood-2/70'
        : selected
          ? 'ring-2 ring-ec-brass'
          : 'ring-1 ring-ec-line-strong';

  const Element = onClick ? 'button' : 'div';

  return (
    <Element
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      title={title}
      aria-pressed={onClick ? selected : undefined}
      className={[
        'relative flex shrink-0 flex-col items-center justify-center rounded-full',
        'bg-gradient-to-br shadow-ec-art transition',
        SIZES[size],
        body,
        ring,
        dimmed ? 'opacity-40' : '',
        state === 'removed' ? 'opacity-60 saturate-50' : '',
        onClick ? 'hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-ec-brass' : ''
      ].join(' ')}
    >
      {/* Sheen — sells the sphere without an image asset. */}
      <span className="pointer-events-none absolute inset-x-2 top-1 h-1/3 rounded-full bg-white/25 blur-[2px]" />
      {isKiller ? (
        <span className="font-display font-bold uppercase leading-none tracking-wide">Killer</span>
      ) : faceDown ? (
        <span className="font-display leading-none opacity-70">?</span>
      ) : (
        <span className="font-serif font-semibold leading-none">{fmtClaim(claim)}</span>
      )}
      {label && (
        <span className="mt-0.5 max-w-full truncate px-1 font-display text-[0.55rem] uppercase tracking-wide opacity-80">
          {label}
        </span>
      )}
      {state === 'yours' && (
        <span
          title="Only you can see this value"
          className="absolute -bottom-1 whitespace-nowrap rounded-full bg-ec-ground px-1 font-display text-[0.5rem] uppercase tracking-wide text-ec-brass"
        >
          only you
        </span>
      )}
    </Element>
  );
}
